import React, { useState } from 'react';
import { logger } from '@/utils/logger';
import { UserGalleryProps, UserGalleryItem } from './types';
import './UserGallery.css';

const UserGallery: React.FC<UserGalleryProps> = ({
    items,
    title = "Kibu is loved!",
    description = "The #KibuHeadphones are loved by kids and parents alike. Share your photos and videos with us on Instagram and tag @kibu.family for a chance to be featured!",
    ctaText = "Instagram",
    ctaLink = "https://www.instagram.com/kibu.family/",
    className = "",
    showCommunitySection = true
}) => {
    const [loadedItems, setLoadedItems] = useState<Set<string>>(new Set());
    const [failedItems, setFailedItems] = useState<Set<string>>(new Set());
    const [retryCount, setRetryCount] = useState<Record<string, number>>({});

    const handleMediaLoad = (url: string) => {
        logger.debug('component', `UserGallery: Media loaded - ${url}`);
        setLoadedItems(prev => new Set(prev).add(url));
    };

    const handleMediaError = (url: string) => {
        logger.error('component', `UserGallery: Failed to load media - ${url}`);
        setFailedItems(prev => new Set(prev).add(url));

        // Retry logic: retry up to 3 times with exponential backoff
        const currentRetry = retryCount[url] ?? 0;
        if (currentRetry < 3) {
            const delay = Math.pow(2, currentRetry) * 1000; // 1s, 2s, 4s
            setTimeout(() => {
                logger.debug('component', `UserGallery: Retrying media load - ${url} (attempt ${currentRetry + 1})`);
                setRetryCount(prev => ({
                    ...prev,
                    [url]: currentRetry + 1
                }));
                // Force a re-render by removing from failed set
                setFailedItems(prev => {
                    const next = new Set(prev);
                    next.delete(url);
                    return next;
                });
            }, delay);
        }
    };

    const isMediaLoaded = (url: string) => {
        return loadedItems.has(url);
    };

    // Duplicate items for seamless infinite scroll
    const duplicatedItems = [...items, ...items];

    const renderGalleryItem = (item: UserGalleryItem, index: number) => {
        const isLoaded = isMediaLoaded(item.url);

        return (
            <div
                key={`${item.url}-${index}`}
                className="user-gallery-item"
                role="img"
                aria-label={item.alt}
            >
                {/* Media content */}
                {item.type === 'video' ? (
                    <video
                        className={`user-gallery-video ${!isLoaded ? 'opacity-0' : 'opacity-100'}`}
                        src={item.url}
                        poster={item.thumbnailUrl}
                        autoPlay={item.autoplay ?? true}
                        loop={item.loop ?? true}
                        muted={item.muted ?? true}
                        playsInline
                        onLoadedData={() => handleMediaLoad(item.url)}
                        onError={() => handleMediaError(item.url)}
                        aria-label={item.alt}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                ) : (
                    <img
                        className={`user-gallery-media ${!isLoaded ? 'opacity-0' : 'opacity-100'}`}
                        src={item.url}
                        alt={item.alt}
                        loading="lazy"
                        onLoad={() => handleMediaLoad(item.url)}
                        onError={() => handleMediaError(item.url)}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                )}

                {/* Loading placeholder or error message */}
                {!isLoaded && (
                    <div className="user-gallery-placeholder">
                        {failedItems.has(item.url) && (retryCount[item.url] ?? 0) >= 3 ? (
                            <div className="user-gallery-error" style={{ textAlign: 'center', padding: '1rem', fontSize: '0.875rem', color: '#999' }}>
                                Unable to load media
                            </div>
                        ) : (
                            <div className="user-gallery-loading-spinner" />
                        )}
                    </div>
                )}

                {/* Overlay with user handle - always visible */}
                <div className="user-gallery-overlay">
                    <span className="user-gallery-handle">
                        {item.alt}
                    </span>
                </div>
            </div>
        );
    };

    return (
        <section className={`user-gallery ${className}`} aria-label="User Generated Content Gallery">
            <div className="user-gallery-container">
                {/* Community Section Header - Centralized */}
                {showCommunitySection && (
                    <header className="user-gallery-header">
                        <h2 className="user-gallery-title">
                            {title}
                        </h2>
                        <p className="user-gallery-description">
                            {description}
                        </p>
                        {/* {ctaLink && ctaText && (
                            <a
                                href={ctaLink}
                                className="user-gallery-cta"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`Visit our ${ctaText} page`}
                            >
                                {ctaText}
                            </a>
                        )} */}
                    </header>
                )}

                {/* Auto-scrolling Gallery */}
                <div className="user-gallery-scroll-container">
                    <div
                        className="user-gallery-scroll-track"
                        aria-label="Scrolling gallery of user photos and videos"
                    >
                        <div className="user-gallery-row">
                            {duplicatedItems.map((item, index) => renderGalleryItem(item, index))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default UserGallery;
